import { useCallback, useMemo } from "react";
import useAuth from "../Hooks/useAuth";
import { DEALER_RETIRED_PERMISSIONS } from "../utils/dealerRetiredPermissions";

/**
 * Permission kontrolü için merkezi hook.
 *
 * admin rolü → tüm yetkilere sahip (wildcard).
 * storage/dealer rolü + management subRole'ü → tüm yetkilere sahip (wildcard).
 * storage/dealer rolleri → auth.permissions dizisi kontrol edilir.
 * customer/enduser/machineworker → permission sistemi dışı (her zaman false).
 * dealer → bayide kaldırılan anahtarlar (DEALER_RETIRED_PERMISSIONS) eski
 * şablonda dursa bile yok sayılır (#302).
 *
 * Wildcard kuralları server tarafındaki `auth.middleware.js:requirePermission`
 * fonksiyonuyla eşleşmelidir.
 *
 * Kullanım:
 *   const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermission();
 *   if (hasPermission("firms.write")) { ... }
 *   if (hasAnyPermission("devices.write", "devices.quota")) { ... }
 */
export default function usePermission() {
  const { auth } = useAuth();

  const userRole = auth?.role;
  const userSubRole = auth?.subRole;
  const userPermissions = useMemo(() => {
    const list = auth?.permissions || [];
    return userRole === "dealer"
      ? list.filter((p) => !DEALER_RETIRED_PERMISSIONS.has(p))
      : list;
  }, [auth?.permissions, userRole]);
  const isAdmin = userRole === "admin";
  // Wildcard (tüm yetkiler) yalnızca admin ve DEPO (storage) management içindir.
  // Bayi (dealer) management artık wildcard DEĞİL — bayi kullanıcıları, alt-rolü
  // management olsa bile yalnızca kendilerine verilen yetkiler kadarını yapar.
  const isManagement = userSubRole === "management" && userRole === "storage";
  const isWildcard = isAdmin || isManagement;

  /** Tek permission kontrolü */
  const hasPermission = useCallback(
    (permission) => {
      if (!permission) return true;
      if (isWildcard) return true;
      return userPermissions.includes(permission);
    },
    [isWildcard, userPermissions]
  );

  /** Herhangi birini sağlıyorsa true */
  const hasAnyPermission = useCallback(
    (...permissions) => {
      return isWildcard || permissions.some((p) => userPermissions.includes(p));
    },
    [isWildcard, userPermissions]
  );

  /** Hepsini sağlıyorsa true */
  const hasAllPermissions = useCallback(
    (...permissions) => {
      if (isWildcard) return true;
      return permissions.every((p) => userPermissions.includes(p));
    },
    [isWildcard, userPermissions]
  );

  return { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin };
}
